import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Send,
  Eye,
  MousePointer,
  AlertTriangle,
  RefreshCw,
  Download,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Inbox,
  Trophy,
  Users,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import { getEmailAnalytics, exportEmailToExcel, EmailAnalytics } from '@/services/analyticsService';

export const EmailAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<EmailAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEmailAnalytics();
      setData(response);
    } catch (err) {
      setError('Erreur lors du chargement des analyses email');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportEmailToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('fr-FR').format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <XCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchData}
            className="ml-auto px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Marketing</h1>
          <p className="text-sm text-gray-500 mt-1">
            Analyse des campagnes email et engagement
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Export...' : 'Exporter Excel'}
          </button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Campaigns */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Campagnes</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {data?.totalCampaigns || 0}
              </p>
              <p className="text-sm text-green-600 mt-2">
                {data?.activeCampaigns || 0} actives
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        {/* Emails Sent */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Emails envoyés</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {formatNumber(data?.totalEmailsSent || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {formatNumber(data?.sendsThisMonth || 0)} ce mois
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Send className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        {/* Open Rate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Taux d'ouverture</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {formatPercent(data?.openRate || 0)}
              </p>
              <p className={`text-sm mt-2 flex items-center gap-1 ${
                (data?.openRate || 0) >= 20 ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {(data?.openRate || 0) >= 20 ? (
                  <CheckCircle size={14} />
                ) : (
                  <AlertTriangle size={14} />
                )}
                {(data?.openRate || 0) >= 20 ? 'Bon' : 'A améliorer'}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Eye className="text-purple-600" size={24} />
            </div>
          </div>
        </div>

        {/* Click Rate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Taux de clic</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {formatPercent(data?.clickRate || 0)}
              </p>
              <p className={`text-sm mt-2 flex items-center gap-1 ${
                (data?.clickRate || 0) >= 2.5 ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {(data?.clickRate || 0) >= 2.5 ? (
                  <CheckCircle size={14} />
                ) : (
                  <AlertTriangle size={14} />
                )}
                {(data?.clickRate || 0) >= 2.5 ? 'Bon' : 'A améliorer'}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <MousePointer className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Health */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-6">Santé de délivrance</h3>
          <div className="space-y-4">
            {/* Delivery Rate */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Taux de délivrance</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatPercent(data?.deliveryRate || 0)}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-500"
                  style={{ width: `${data?.deliveryRate || 0}%` }}
                />
              </div>
            </div>

            {/* Bounce Rate */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Taux de rebond</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatPercent(data?.bounceRate || 0)}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    (data?.bounceRate || 0) <= 2 ? 'bg-green-500' :
                    (data?.bounceRate || 0) <= 5 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(data?.bounceRate || 0, 100)}%` }}
                />
              </div>
            </div>

            {/* Unsubscribe Rate */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Taux de désabonnement</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatPercent(data?.unsubscribeRate || 0)}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    (data?.unsubscribeRate || 0) <= 0.5 ? 'bg-green-500' :
                    (data?.unsubscribeRate || 0) <= 1 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min((data?.unsubscribeRate || 0) * 10, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Health Score */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Score de santé</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                (data?.deliveryRate || 0) >= 95 && (data?.bounceRate || 0) <= 2
                  ? 'bg-green-100 text-green-800'
                  : (data?.deliveryRate || 0) >= 90
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {(data?.deliveryRate || 0) >= 95 && (data?.bounceRate || 0) <= 2
                  ? 'Excellent'
                  : (data?.deliveryRate || 0) >= 90
                  ? 'Bon'
                  : 'A améliorer'}
              </span>
            </div>
          </div>
        </div>

        {/* Top Campaigns */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Meilleures campagnes</h3>
            <Trophy className="text-yellow-500" size={20} />
          </div>
          {data?.bestCampaigns && data.bestCampaigns.length > 0 ? (
            <div className="space-y-4">
              {data.bestCampaigns.map((campaign, index) => (
                <div
                  key={campaign.id}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    index === 0 ? 'bg-yellow-100 text-yellow-600' :
                    index === 1 ? 'bg-gray-200 text-gray-600' :
                    index === 2 ? 'bg-orange-100 text-orange-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {campaign.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatNumber(campaign.totalSent)} envoyés
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {formatPercent(campaign.openRate)}
                    </p>
                    <p className="text-xs text-gray-500">ouverture</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Inbox className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-gray-500">Aucune campagne envoyée</p>
            </div>
          )}
        </div>
      </div>

      {/* List Health */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-6">Santé de la liste</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total Subscribers */}
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Users className="text-blue-600" size={20} />
            </div>
            <p className="text-2xl font-bold text-blue-900">
              {formatNumber(data?.listHealth?.totalSubscribers || 0)}
            </p>
            <p className="text-sm text-blue-600">Abonnés totaux</p>
          </div>

          {/* New Recipients This Month */}
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <UserPlus className="text-green-600" size={20} />
            </div>
            <p className="text-2xl font-bold text-green-900">
              +{formatNumber(data?.listHealth?.newRecipientsThisMonth || 0)}
            </p>
            <p className="text-sm text-green-600">Nouveaux ce mois</p>
          </div>

          {/* Unsubscribes This Month */}
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <UserMinus className="text-red-600" size={20} />
            </div>
            <p className="text-2xl font-bold text-red-900">
              -{formatNumber(data?.listHealth?.unsubscribesThisMonth || 0)}
            </p>
            <p className="text-sm text-red-600">Désabonnements</p>
          </div>

          {/* List Growth Rate */}
          <div className={`text-center p-4 rounded-lg ${
            (data?.listHealth?.listGrowthRate || 0) >= 0 ? 'bg-emerald-50' : 'bg-orange-50'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
              (data?.listHealth?.listGrowthRate || 0) >= 0 ? 'bg-emerald-100' : 'bg-orange-100'
            }`}>
              {(data?.listHealth?.listGrowthRate || 0) >= 0 ? (
                <TrendingUp className="text-emerald-600" size={20} />
              ) : (
                <TrendingDown className="text-orange-600" size={20} />
              )}
            </div>
            <p className={`text-2xl font-bold ${
              (data?.listHealth?.listGrowthRate || 0) >= 0 ? 'text-emerald-900' : 'text-orange-900'
            }`}>
              {(data?.listHealth?.listGrowthRate || 0) >= 0 ? '+' : ''}{(data?.listHealth?.listGrowthRate || 0).toFixed(1)}%
            </p>
            <p className={`text-sm ${
              (data?.listHealth?.listGrowthRate || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'
            }`}>Croissance de la liste</p>
          </div>
        </div>
      </div>

      {/* Monthly Comparison */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Comparaison mensuelle</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Ce mois</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {formatNumber(data?.sendsThisMonth || 0)}
            </p>
            <p className="text-sm text-gray-500">emails envoyés</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Mois dernier</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {formatNumber(data?.sendsLastMonth || 0)}
            </p>
            <p className="text-sm text-gray-500">emails envoyés</p>
          </div>
        </div>
        {(data?.sendsLastMonth || 0) > 0 && (
          <div className={`mt-4 p-3 rounded-lg ${
            (data?.sendsThisMonth || 0) >= (data?.sendsLastMonth || 0)
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {(data?.sendsThisMonth || 0) >= (data?.sendsLastMonth || 0) ? (
                <TrendingUp size={16} />
              ) : (
                <TrendingDown size={16} />
              )}
              <span className="text-sm font-medium">
                {(((data?.sendsThisMonth || 0) - (data?.sendsLastMonth || 0)) / (data?.sendsLastMonth || 1) * 100).toFixed(1)}%
                {(data?.sendsThisMonth || 0) >= (data?.sendsLastMonth || 0) ? ' de plus' : ' de moins'} que le mois dernier
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailAnalyticsPage;
